import ActionsButton             from '@/components/actionsButton/ActionsButton';
import { Box }                   from '@chakra-ui/react';
import DaisukiModel              from '@/models/services/website/DaisukiModel';
import DeleteModal               from '@/components/deleteModal/DeleteModal';
import EventModel                from '@/models/services/website/EventModel';
import Image                     from '@/components/Image/Image';
import ParticipationModel        from '@/models/services/website/ParticipationModel';
import links                     from '@/tools/links';
import PlaceModel                from '@/models/services/website/PlaceModel';
import React                     from 'react';
import Spinner                   from '@/components/spinner/Spinner';
import { Text }                  from '@chakra-ui/react';
import dayjs                     from 'dayjs';
import { toaster }               from '@/components/ui/toaster';
import { useTranslation }        from 'react-i18next';
import i18n                      from '@/i18n.config';
import WebsiteEntertainmentModel from '@/models/services/website/WebsiteEntertainmentModel';
import { Link }                  from 'react-router-dom';
import StandPageModel            from '@/models/services/website/StandPageModel';

interface IDaisukiListRowProps {
	daisuki: DaisukiModel;
	websiteEntertainment: WebsiteEntertainmentModel;
	resource: EventModel | ParticipationModel | StandPageModel | PlaceModel | null;
}

const DaisukiListRow = (props: IDaisukiListRowProps) => {
	const { daisuki, resource, websiteEntertainment } = props;
	const title = resource instanceof StandPageModel ? resource.stand.title : resource?.getTitle(i18n.language);

	const getResourceUrl = (): string => {
		if (!resource || !websiteEntertainment.website?.url) return '';
		const baseUrl = websiteEntertainment.website.url;
		const year = websiteEntertainment.slugPrefix;
		const slug = resource instanceof StandPageModel ? resource.stand.slug : resource.slug;
		const lang = i18n.language;

		if (resource instanceof EventModel) {
			return `${baseUrl}/${links.event(lang, year, slug)}`;
		} else if (resource instanceof ParticipationModel) {
			return `${baseUrl}/${links.participation(lang, year, slug)}`;
		} else if (resource instanceof StandPageModel) {
			return `${baseUrl}/${links.stand(lang, year, slug)}`;
		} else if (resource instanceof PlaceModel) {
			return `${baseUrl}/${links.place(lang, year, slug)}`;
		}
		return '';
	};

	const { t } = useTranslation();
	const [isLoading, setIsLoading] = React.useState(false);
	const [deleteModalIsOpen, setDeleteModalIsOpen] = React.useState(false);

	const removeDaisuki = async () => {
		try {
			setIsLoading(true);

			await daisuki.destroy();

			toaster.create({
				title: t('pages.daisuki.actions.destroy.success'),
				type: 'success',
			})
		} catch (error) {
			toaster.create({
				title: t('pages.daisuki.actions.destroy.error'),
				type: 'error',
			})
			console.log(error);
		} finally {
			setIsLoading(false);
			setDeleteModalIsOpen(false);
		}
	};

	if (isLoading) {
		return <Spinner />
	}

	return (
		<Box
			display="flex"
			alignItems="center"
			justifyContent="space-between"
		>
			<DeleteModal
				modalIsOpen={deleteModalIsOpen}
				deleteMessage={t('pages.daisuki.actions.destroy.confirmDeletion')}
				onCancel={() => setDeleteModalIsOpen(false)}
				onConfirm={removeDaisuki}
			/>
			<Link to={getResourceUrl()} target="_blank" rel="noopener noreferrer" style={{ width: '100%' }}>
				<Box
					display="flex"
					alignItems="center"
					gap="25px"
				>
					<Box
						minWidth="75px"
						maxWidth="75px"
						height="75px"
						overflow="hidden"
						borderRadius="md"
					>
						<Image errorSize='xl' src={resource?.getFiles(['event_picture', 'participation_picture', 'place_image', 'place_image', 'stand_page_image']).at(0)?.url} width="75px" height="75px" objectFit="cover" />
					</Box>
					<Box
						display="flex"
						flexDirection="column"
						gap="5px"
						paddingRight="25px"
					>
						<Box><Text className="poppins-semibold" fontSize="16px" textDecoration="underline">{title}</Text></Box>
						<Box fontSize="12px">
							<Text>{t('pages.daisuki.addDate', { date: dayjs(daisuki.createdAt).format('DD/MM/YYYY') })}</Text>
						</Box>
					</Box>
				</Box>
			</Link>
			<ActionsButton actions={[{ label: t('pages.daisuki.actions.destroy.label'), do: () => setDeleteModalIsOpen(true)}]} />
		</Box>
	)
}

export default DaisukiListRow;